import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../../services/authService';
import { getUserType } from '../../utils/roles';
import './Pricing.css';

const Pricing = () => {
  const [user, setUser] = useState(null);
  const [userLoaded, setUserLoaded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try { 
        const u = await authService.getUserInfo(); 
        setUser(u); 
      } catch {} 
      finally { 
        setUserLoaded(true); 
      }
    })();
  }, []);

  const userType = useMemo(() => getUserType(user), [user]);
  const isTestDrive = useMemo(() => {
    try {
      const email = (user && (user.Email || user.email || '')) || '';
      return !!email && email.toLowerCase().startsWith('testdrive');
    } catch { return false; }
  }, [user]);

  // Determine Pro button label and action
  const proButtonLabel = useMemo(() => {
    if (!userLoaded) return 'Loading…';
    if (isTestDrive) return 'Top-ups disabled (TestDrive)';
    return (userType === 'Pro' || userType === 'Admin') ? 'Top Up' : 'Upgrade to Pro';
  }, [userLoaded, isTestDrive, userType]);

  const handleProButtonClick = () => {
    if (!userLoaded || isTestDrive) return;
    navigate('/top-up');
  };

  const handleFreeButtonClick = () => {
    navigate('/register');
  };

  // Styles
  const styles = {
    container: {
      maxWidth: '1400px',
      margin: '0 auto',
      padding: '60px 20px',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      background: '#f8f9fa',
      color: '#1a1a1a',
      lineHeight: 1.6
    },
    h1: {
      textAlign: 'center',
      fontSize: '2.5rem',
      marginBottom: '1rem',
      color: '#1a1a1a'
    },
    subtitle: {
      textAlign: 'center',
      fontSize: '1.1rem',
      color: '#666',
      marginBottom: '50px'
    },
    pricingTable: {
      background: 'white',
      borderRadius: '12px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      overflow: 'hidden'
    },
    pricingHeader: {
      display: 'grid',
      gridTemplateColumns: '300px repeat(4, 1fr)',
      borderBottom: '2px solid #e5e7eb',
      background: 'linear-gradient(to bottom, #fafafa 0%, #ffffff 100%)'
    },
    featureLabelHeader: {
      padding: '30px 20px',
      fontWeight: 600,
      fontSize: '0.9rem',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      color: '#6b7280',
      borderRight: '1px solid #e5e7eb'
    },
    planHeader: {
      padding: '30px 20px',
      textAlign: 'center',
      borderRight: '1px solid #e5e7eb',
      position: 'relative'
    },
    planHeaderHighlighted: {
      padding: '30px 20px',
      textAlign: 'center',
      borderRight: '1px solid #e5e7eb',
      position: 'relative',
      background: 'linear-gradient(135deg, #5b21b6 0%, #7c3aed 100%)',
      color: 'white'
    },
    popularBadge: {
      position: 'absolute',
      top: '10px',
      left: '50%',
      transform: 'translateX(-50%)',
      background: '#fbbf24',
      color: '#78350f',
      padding: '4px 12px',
      borderRadius: '20px',
      fontSize: '0.75rem',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    planName: {
      fontSize: '1.5rem',
      fontWeight: 700,
      marginBottom: '8px',
      color: '#1a1a1a'
    },
    planNameHighlighted: {
      fontSize: '1.5rem',
      fontWeight: 700,
      marginBottom: '8px',
      color: 'white'
    },
    planPrice: {
      fontSize: '1.8rem',
      fontWeight: 700,
      color: '#5b21b6',
      marginBottom: '5px'
    },
    planPriceHighlighted: {
      fontSize: '1.8rem',
      fontWeight: 700,
      color: 'white',
      marginBottom: '5px'
    },
    planPriceNote: {
      fontSize: '0.85rem',
      color: '#6b7280',
      marginBottom: '15px'
    },
    planPriceNoteHighlighted: {
      fontSize: '0.85rem',
      color: 'white',
      marginBottom: '15px'
    },
    planCta: {
      display: 'inline-block',
      padding: '10px 24px',
      borderRadius: '6px',
      fontWeight: 600,
      fontSize: '0.9rem',
      textDecoration: 'none',
      transition: 'all 0.2s',
      cursor: 'pointer',
      border: 'none',
      fontFamily: 'inherit'
    },
    planCtaPrimary: {
      background: '#5b21b6',
      color: 'white'
    },
    planCtaPrimaryHover: {
      background: '#4c1d95',
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 12px rgba(91, 33, 182, 0.3)'
    },
    planCtaSecondary: {
      background: '#f3f4f6',
      color: '#374151'
    },
    planCtaSecondaryHover: {
      background: '#e5e7eb'
    },
    planCtaDisabled: {
      opacity: 0.6,
      cursor: 'not-allowed'
    },
    featureCategory: {
      display: 'grid',
      gridTemplateColumns: '300px repeat(4, 1fr)',
      borderBottom: '1px solid #e5e7eb'
    },
    categoryName: {
      padding: '16px 20px',
      fontWeight: 700,
      fontSize: '1rem',
      background: '#f9fafb',
      color: '#1a1a1a',
      borderRight: '1px solid #e5e7eb',
      display: 'flex',
      alignItems: 'center'
    },
    categoryIcon: {
      marginRight: '10px',
      fontSize: '1.2rem'
    },
    categoryPlaceholder: {
      borderRight: '1px solid #e5e7eb'
    },
    featureRow: {
      display: 'grid',
      gridTemplateColumns: '300px repeat(4, 1fr)',
      borderBottom: '1px solid #f3f4f6'
    },
    featureName: {
      padding: '16px 20px',
      paddingLeft: '40px',
      fontSize: '0.9rem',
      color: '#374151',
      borderRight: '1px solid #e5e7eb',
      display: 'flex',
      alignItems: 'center'
    },
    featureValue: {
      padding: '16px 20px',
      textAlign: 'center',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '0.9rem',
      borderRight: '1px solid #e5e7eb'
    },
    featureValueLast: {
      padding: '16px 20px',
      textAlign: 'center',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '0.9rem'
    },
    check: {
      color: '#10b981',
      fontSize: '1.2rem',
      fontWeight: 'bold'
    },
    cross: {
      color: '#d1d5db',
      fontSize: '1.2rem'
    },
    featureText: {
      color: '#6b7280',
      fontSize: '0.85rem'
    },
    footerNote: {
      textAlign: 'center',
      marginTop: '40px',
      color: '#6b7280',
      fontSize: '0.9rem'
    },
    footerNoteLink: {
      color: '#5b21b6',
      textDecoration: 'none'
    },
    footerNoteLinkHover: {
      textDecoration: 'underline'
    },
    '@media (max-width: 1200px)': {
      pricingHeader: {
        gridTemplateColumns: '250px repeat(4, 1fr)'
      },
      featureCategory: {
        gridTemplateColumns: '250px repeat(4, 1fr)'
      },
      featureRow: {
        gridTemplateColumns: '250px repeat(4, 1fr)'
      }
    },
    '@media (max-width: 900px)': {
      container: {
        padding: '30px 10px'
      },
      pricingTable: {
        overflowX: 'auto'
      },
      pricingHeader: {
        minWidth: '900px'
      },
      featureCategory: {
        minWidth: '900px'
      },
      featureRow: {
        minWidth: '900px'
      }
    }
  };

  const FeatureRow = ({ name, free, pro, teams, enterprise }) => (
    <div style={styles.featureRow} className="feature-row-responsive">
      <div style={styles.featureName}>{name}</div>
      <div style={styles.featureValue} className="feature-value">{free}</div>
      <div style={styles.featureValue} className="feature-value">{pro}</div>
      <div style={styles.featureValue} className="feature-value">{teams}</div>
      <div style={styles.featureValueLast} className="feature-value">{enterprise}</div>
    </div>
  );

  const FeatureCategory = ({ icon, name }) => (
    <div style={styles.featureCategory} className="feature-category-responsive">
      <div style={styles.categoryName}>
        <span style={styles.categoryIcon}>{icon}</span>
        {name}
      </div>
      <div style={styles.categoryPlaceholder}></div>
      <div style={styles.categoryPlaceholder}></div>
      <div style={styles.categoryPlaceholder}></div>
      <div style={styles.categoryPlaceholder}></div>
    </div>
  );

  const isPro = userLoaded && (userType === 'Pro' || userType === 'Admin');
  const isFree = userLoaded && userType === 'Free';

  return (
    <div style={styles.container} className="pricing-container-responsive">
      <h1 style={styles.h1}>Plans & Pricing</h1>
      <p style={styles.subtitle}>Choose the plan that fits your needs. Upgrade or downgrade anytime.</p>

      <div style={styles.pricingTable} className="pricing-table-responsive">
        {/* Header with Pricing */}
        <div style={styles.pricingHeader} className="pricing-header-responsive">
          <div style={styles.featureLabelHeader}>Features</div>
          
          {/* FREE */}
          <div style={styles.planHeader} className="plan-header">
            <div style={styles.planName}>Free</div>
            <div style={styles.planPrice}>$0</div>
            <div style={styles.planPriceNote}>Forever</div>
            <button
              onClick={handleFreeButtonClick}
              style={{
                ...styles.planCta,
                ...styles.planCtaSecondary
              }}
              onMouseEnter={(e) => {
                if (!e.target.disabled) {
                  Object.assign(e.target.style, styles.planCtaSecondaryHover);
                }
              }}
              onMouseLeave={(e) => {
                if (!e.target.disabled) {
                  Object.assign(e.target.style, styles.planCtaSecondary);
                }
              }}
            >
              Get Started
            </button>
          </div>

          {/* PRO */}
          <div style={styles.planHeaderHighlighted} className="plan-header">
            <span style={styles.popularBadge}>Most Popular</span>
            <div style={styles.planNameHighlighted}>Pro</div>
            <div style={styles.planPriceHighlighted}>Pay-as-you-go</div>
            <div style={styles.planPriceNoteHighlighted}>$5 minimum deposit</div>
            <button
              onClick={handleProButtonClick}
              disabled={!userLoaded || isTestDrive}
              style={{
                ...styles.planCta,
                ...styles.planCtaPrimary,
                ...((!userLoaded || isTestDrive) ? styles.planCtaDisabled : {})
              }}
              onMouseEnter={(e) => {
                if (!e.target.disabled) {
                  Object.assign(e.target.style, styles.planCtaPrimaryHover);
                }
              }}
              onMouseLeave={(e) => {
                if (!e.target.disabled) {
                  Object.assign(e.target.style, styles.planCtaPrimary);
                }
              }}
            >
              {proButtonLabel}
            </button>
          </div>

          {/* TEAMS */}
          <div style={styles.planHeader} className="plan-header">
            <div style={styles.planName}>Teams</div>
            <div style={styles.planPrice}>$15</div>
            <div style={styles.planPriceNote}>per user/month (5 min)</div>
            <a
              href="mailto:support@pocketpromptengineer.com?subject=Teams Plan Inquiry"
              style={{
                ...styles.planCta,
                ...styles.planCtaSecondary
              }}
              onMouseEnter={(e) => {
                Object.assign(e.target.style, styles.planCtaSecondaryHover);
              }}
              onMouseLeave={(e) => {
                Object.assign(e.target.style, styles.planCtaSecondary);
              }}
            >
              Contact Sales
            </a>
          </div>

          {/* ENTERPRISE */}
          <div style={styles.planHeader} className="plan-header">
            <div style={styles.planName}>Enterprise</div>
            <div style={styles.planPrice}>Custom</div>
            <div style={styles.planPriceNote}>Contact for pricing</div>
            <a
              href="mailto:support@pocketpromptengineer.com?subject=Enterprise Inquiry"
              style={{
                ...styles.planCta,
                ...styles.planCtaSecondary
              }}
              onMouseEnter={(e) => {
                Object.assign(e.target.style, styles.planCtaSecondaryHover);
              }}
              onMouseLeave={(e) => {
                Object.assign(e.target.style, styles.planCtaSecondary);
              }}
            >
              Contact Sales
            </a>
          </div>
        </div>

        {/* AI MODELS & ACCESS */}
        <FeatureCategory icon="🤖" name="AI Models & Access" />
        <FeatureRow
          name="AI Transactions per day"
          free={<span style={styles.featureText}>10 per day</span>}
          pro={<span style={styles.featureText}>Unlimited</span>}
          teams={<span style={styles.featureText}>Unlimited</span>}
          enterprise={<span style={styles.featureText}>Unlimited</span>}
        />
        <FeatureRow
          name="Available AI models"
          free={<span style={styles.featureText}>2 models</span>}
          pro={<span style={styles.featureText}>All 11 models</span>}
          teams={<span style={styles.featureText}>All 11 models</span>}
          enterprise={<span style={styles.featureText}>All 11 + BYOK</span>}
        />
        <FeatureRow
          name="Work Mode & Chat Mode"
          free={<span style={styles.cross}>✕</span>}
          pro={<span style={styles.check}>✓</span>}
          teams={<span style={styles.check}>✓</span>}
          enterprise={<span style={styles.check}>✓</span>}
        />
        <FeatureRow
          name="Express & Studio modes"
          free={<span style={styles.featureText}>Express only</span>}
          pro={<span style={styles.check}>✓</span>}
          teams={<span style={styles.check}>✓</span>}
          enterprise={<span style={styles.check}>✓</span>}
        />
        <FeatureRow
          name="Maximum output tokens"
          free={<span style={styles.featureText}>4,096</span>}
          pro={<span style={styles.featureText}>32,768</span>}
          teams={<span style={styles.featureText}>32,768</span>}
          enterprise={<span style={styles.featureText}>32,768</span>}
        />

        {/* PROMPT FEATURES */}
        <FeatureCategory icon="✨" name="Prompt Features" />
        <FeatureRow
          name="Prompt Generator"
          free={<span style={styles.check}>✓</span>}
          pro={<span style={styles.check}>✓</span>}
          teams={<span style={styles.check}>✓</span>}
          enterprise={<span style={styles.check}>✓</span>}
        />
        <FeatureRow
          name="Prompt Optimization"
          free={<span style={styles.featureText}>Limited</span>}
          pro={<span style={styles.featureText}>Unlimited</span>}
          teams={<span style={styles.featureText}>Unlimited</span>}
          enterprise={<span style={styles.featureText}>Unlimited</span>}
        />
        <FeatureRow
          name="Multi-Send (compare models)"
          free={<span style={styles.cross}>✕</span>}
          pro={<span style={styles.check}>✓</span>}
          teams={<span style={styles.check}>✓</span>}
          enterprise={<span style={styles.check}>✓</span>}
        />
        <FeatureRow
          name="Export formats"
          free={<span style={styles.featureText}>Text only</span>}
          pro={<span style={styles.featureText}>5 formats</span>}
          teams={<span style={styles.featureText}>5 formats</span>}
          enterprise={<span style={styles.featureText}>5 formats</span>}
        />

        {/* BAIQL AUTOMATION */}
        <FeatureCategory icon="🥯" name="BAIQL Automation" />
        <FeatureRow
          name="BAIQL Editor access"
          free={<span style={styles.featureText}>View only ($10 to execute)</span>}
          pro={<span style={styles.check}>✓</span>}
          teams={<span style={styles.check}>✓</span>}
          enterprise={<span style={styles.check}>✓</span>}
        />
        <FeatureRow
          name="BAIQL script executions"
          free={<span style={styles.featureText}>Requires $10 balance</span>}
          pro={<span style={styles.featureText}>25/month ($25 min balance)</span>}
          teams={<span style={styles.featureText}>Unlimited</span>}
          enterprise={<span style={styles.featureText}>Unlimited</span>}
        />
        <FeatureRow
          name="BAIQL Blocks (visual builder)"
          free={<span style={styles.check}>✓</span>}
          pro={<span style={styles.check}>✓</span>}
          teams={<span style={styles.check}>✓</span>}
          enterprise={<span style={styles.check}>✓</span>}
        />
        <FeatureRow
          name="BAIQL Code (text editor)"
          free={<span style={styles.check}>✓</span>}
          pro={<span style={styles.check}>✓</span>}
          teams={<span style={styles.check}>✓</span>}
          enterprise={<span style={styles.check}>✓</span>}
        />
        <FeatureRow
          name="Script storage"
          free={<span style={styles.featureText}>10 scripts</span>}
          pro={<span style={styles.featureText}>100 scripts</span>}
          teams={<span style={styles.featureText}>Unlimited</span>}
          enterprise={<span style={styles.featureText}>Unlimited</span>}
        />

        {/* COLLABORATION */}
        <FeatureCategory icon="👥" name="Collaboration & Sharing" />
        <FeatureRow
          name="Prompt sharing"
          free={<span style={styles.featureText}>Community</span>}
          pro={<span style={styles.featureText}>Community</span>}
          teams={<span style={styles.featureText}>Private team + community</span>}
          enterprise={<span style={styles.featureText}>Private team + community</span>}
        />
        <FeatureRow
          name="Team management"
          free={<span style={styles.cross}>✕</span>}
          pro={<span style={styles.cross}>✕</span>}
          teams={<span style={styles.check}>✓</span>}
          enterprise={<span style={styles.check}>✓</span>}
        />
        <FeatureRow
          name="Saved conversations"
          free={<span style={styles.featureText}>10 max</span>}
          pro={<span style={styles.featureText}>100 max</span>}
          teams={<span style={styles.featureText}>Unlimited</span>}
          enterprise={<span style={styles.featureText}>Unlimited</span>}
        />
        <FeatureRow
          name="Conversation context retention"
          free={<span style={styles.featureText}>15 pairs</span>}
          pro={<span style={styles.featureText}>15 pairs</span>}
          teams={<span style={styles.featureText}>Full history</span>}
          enterprise={<span style={styles.featureText}>Full history</span>}
        />

        {/* SECURITY & COMPLIANCE */}
        <FeatureCategory icon="🔒" name="Security & Compliance" />
        <FeatureRow
          name="PII detection & redaction"
          free={<span style={styles.check}>✓</span>}
          pro={<span style={styles.check}>✓</span>}
          teams={<span style={styles.check}>✓</span>}
          enterprise={<span style={styles.check}>✓</span>}
        />
        <FeatureRow
          name="Content moderation"
          free={<span style={styles.featureText}>Standard</span>}
          pro={<span style={styles.featureText}>Standard</span>}
          teams={<span style={styles.featureText}>Customizable on request</span>}
          enterprise={<span style={styles.featureText}>Advanced + custom rules</span>}
        />
        <FeatureRow
          name="Virus scanning"
          free={<span style={styles.check}>✓</span>}
          pro={<span style={styles.check}>✓</span>}
          teams={<span style={styles.check}>✓</span>}
          enterprise={<span style={styles.check}>✓</span>}
        />
        <FeatureRow
          name="Single Sign-On (SSO)"
          free={<span style={styles.cross}>✕</span>}
          pro={<span style={styles.cross}>✕</span>}
          teams={<span style={styles.cross}>✕</span>}
          enterprise={<span style={styles.check}>✓</span>}
        />
        <FeatureRow
          name="Audit logs & compliance"
          free={<span style={styles.cross}>✕</span>}
          pro={<span style={styles.cross}>✕</span>}
          teams={<span style={styles.cross}>✕</span>}
          enterprise={<span style={styles.check}>✓</span>}
        />
        <FeatureRow
          name="Role-based permissions"
          free={<span style={styles.cross}>✕</span>}
          pro={<span style={styles.cross}>✕</span>}
          teams={<span style={styles.featureText}>Basic roles</span>}
          enterprise={<span style={styles.featureText}>Custom roles</span>}
        />

        {/* ADMIN & MANAGEMENT */}
        <FeatureCategory icon="⚙️" name="Admin & Management" />
        <FeatureRow
          name="Admin dashboard"
          free={<span style={styles.featureText}>Personal</span>}
          pro={<span style={styles.featureText}>Personal</span>}
          teams={<span style={styles.featureText}>Team admin controls</span>}
          enterprise={<span style={styles.featureText}>Enterprise admin controls</span>}
        />
        <FeatureRow
          name="User management"
          free={<span style={styles.cross}>✕</span>}
          pro={<span style={styles.cross}>✕</span>}
          teams={<span style={styles.check}>✓</span>}
          enterprise={<span style={styles.check}>✓</span>}
        />
        <FeatureRow
          name="Usage analytics"
          free={<span style={styles.featureText}>Personal only</span>}
          pro={<span style={styles.featureText}>Personal only</span>}
          teams={<span style={styles.featureText}>Team metrics</span>}
          enterprise={<span style={styles.featureText}>Full analytics suite</span>}
        />
        <FeatureRow
          name="Budget controls & quotas"
          free={<span style={styles.cross}>✕</span>}
          pro={<span style={styles.cross}>✕</span>}
          teams={<span style={styles.cross}>✕</span>}
          enterprise={<span style={styles.check}>✓</span>}
        />
        <FeatureRow
          name="API access"
          free={<span style={styles.cross}>✕</span>}
          pro={<span style={styles.cross}>✕</span>}
          teams={<span style={styles.cross}>✕</span>}
          enterprise={<span style={styles.check}>✓</span>}
        />

        {/* SUPPORT */}
        <FeatureCategory icon="💬" name="Support & Services" />
        <FeatureRow
          name="Support level"
          free={<span style={styles.featureText}>Community</span>}
          pro={<span style={styles.featureText}>Email</span>}
          teams={<span style={styles.featureText}>Priority email</span>}
          enterprise={<span style={styles.featureText}>Dedicated support</span>}
        />
        <FeatureRow
          name="Onboarding & training"
          free={<span style={styles.cross}>✕</span>}
          pro={<span style={styles.cross}>✕</span>}
          teams={<span style={styles.featureText}>Self-service</span>}
          enterprise={<span style={styles.featureText}>White-glove onboarding</span>}
        />
        <FeatureRow
          name="SLA guarantee"
          free={<span style={styles.cross}>✕</span>}
          pro={<span style={styles.cross}>✕</span>}
          teams={<span style={styles.cross}>✕</span>}
          enterprise={<span style={styles.featureText}>Up to 99.9% uptime</span>}
        />
        <FeatureRow
          name="Deployment options"
          free={<span style={styles.featureText}>Cloud only</span>}
          pro={<span style={styles.featureText}>Cloud only</span>}
          teams={<span style={styles.featureText}>Cloud only</span>}
          enterprise={<span style={styles.featureText}>Cloud + Private</span>}
        />
      </div>

      <div style={styles.footerNote}>
        All plans include transparent token-based pricing with no hidden fees. <br />
        Need help choosing? <a href="mailto:support@pocketpromptengineer.com" style={styles.footerNoteLink}>Contact our sales team</a>
      </div>
    </div>
  );
};

export default Pricing;
